/**
 * CoverageSection — presentational React component for the Coverage
 * sub-tab. Takes a CoverageData snapshot as props (parsed by
 * coverageStore.ts from sandbox persistence files) and renders:
 *
 *   - Empty state when no reconciler data exists
 *   - Graph stats summary (node/edge counts by source + kind)
 *   - Calibration health (decision count, mean confidence, accuracy
 *     among resolved items)
 *   - Review queue items grouped by reason, with verdict badges
 *
 * Pure presentational — no side effects, no hooks, no localStorage.
 * Game-agnostic by construction: just shapes data for display.
 */

import type {
  CoverageData,
  CoverageReviewItem,
  ReviewReason,
} from './coverageStore';

// ─────────────────────────────────────────────────────────────────────
// Reason metadata
// ─────────────────────────────────────────────────────────────────────

const REASON_LABEL: Record<ReviewReason, string> = {
  'low-confidence':         'Low confidence',
  'verifier-failed':        'Verifier failed',
  'no-confidence':          'No measurable confidence',
  'post-validation-failed': 'Failed post-validation',
};

const REASON_BLURB: Record<ReviewReason, string> = {
  'low-confidence':         'Stage 1 confidence below the routing floor — needs human judgment.',
  'verifier-failed':        'Stage 2 grounding check could not produce a verdict (LLM error / malformed output).',
  'no-confidence':          'No logprobs and no verbalised confidence available for this verdict.',
  'post-validation-failed': 'Verdict was salvageable but failed structural checks (e.g. partial phantom).',
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

export interface CoverageSectionProps {
  data: CoverageData;
}

export function CoverageSection({ data }: CoverageSectionProps) {
  if (data.empty) {
    return (
      <div className="dd-coverage-empty">
        <h3>No reconciler activity yet</h3>
        <p>
          Once the design-graph reconciler has run at least once, this view
          will show the review queue, graph density, and calibration health.
        </p>
      </div>
    );
  }

  return (
    <div className="dd-coverage">
      <Warnings warnings={data.warnings} />
      <GraphStatsCard stats={data.graphStats} />
      <CalibrationCard stats={data.calibrationStats} />
      <ReviewQueueCard items={data.reviewQueue} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Warnings
// ─────────────────────────────────────────────────────────────────────

function Warnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="dd-coverage-warnings" role="alert">
      <strong>Warnings ({warnings.length}):</strong>
      <ul>
        {warnings.map((w, i) => (<li key={i}>{w}</li>))}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Graph stats card
// ─────────────────────────────────────────────────────────────────────

function GraphStatsCard({ stats }: { stats: CoverageData['graphStats'] }) {
  const sortedKinds = Object.entries(stats.edgesByKind).sort((a, b) => b[1] - a[1]);
  return (
    <section className="dd-coverage-card" aria-labelledby="dd-graph-stats-h">
      <h3 id="dd-graph-stats-h">Graph density</h3>
      <div className="dd-coverage-stat-grid">
        <Stat label="Nodes" value={stats.nodes} />
        <Stat label="Edges" value={stats.edges} />
        <Stat label="Manual" value={stats.edgesBySource.manual} small />
        <Stat label="Derived (static)" value={stats.edgesBySource.derived} small />
        <Stat label="LLM-derived" value={stats.edgesBySource.llmDerived} small />
      </div>
      {sortedKinds.length > 0 && (
        <details className="dd-coverage-details">
          <summary>By edge kind</summary>
          <ul className="dd-coverage-kind-list">
            {sortedKinds.map(([kind, count]) => (
              <li key={kind}><span className="dd-coverage-kind">{kind}</span><span>{count}</span></li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Calibration card
// ─────────────────────────────────────────────────────────────────────

function CalibrationCard({ stats }: { stats: CoverageData['calibrationStats'] }) {
  const total = stats.decisions + stats.resolutions;
  return (
    <section className="dd-coverage-card" aria-labelledby="dd-cal-h">
      <h3 id="dd-cal-h">Calibration health</h3>
      {total === 0 ? (
        <p className="dd-muted">No decisions recorded yet.</p>
      ) : (
        <div className="dd-coverage-stat-grid">
          <Stat label="Decisions" value={stats.decisions} />
          <Stat label="Resolutions" value={stats.resolutions} />
          <Stat label="Mean conf" value={fmtPct(stats.meanConfidence)} small />
          <Stat
            label="Mean conf (correct)"
            value={stats.meanConfidenceCorrect === null ? '—' : fmtPct(stats.meanConfidenceCorrect)}
            small
          />
          <Stat
            label="Mean conf (incorrect)"
            value={stats.meanConfidenceIncorrect === null ? '—' : fmtPct(stats.meanConfidenceIncorrect)}
            small
          />
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Review queue card
// ─────────────────────────────────────────────────────────────────────

function ReviewQueueCard({ items }: { items: CoverageReviewItem[] }) {
  if (items.length === 0) {
    return (
      <section className="dd-coverage-card" aria-labelledby="dd-queue-h">
        <h3 id="dd-queue-h">Review queue</h3>
        <p className="dd-muted">No items pending review. The reconciler is up to date.</p>
      </section>
    );
  }

  // Group by reason for clearer scanning.
  const byReason = new Map<ReviewReason, CoverageReviewItem[]>();
  for (const item of items) {
    const list = byReason.get(item.reason) ?? [];
    list.push(item);
    byReason.set(item.reason, list);
  }

  return (
    <section className="dd-coverage-card" aria-labelledby="dd-queue-h">
      <h3 id="dd-queue-h">Review queue ({items.length})</h3>
      {[...byReason.entries()].map(([reason, group]) => (
        <div key={reason} className="dd-coverage-queue-group">
          <div className="dd-coverage-queue-group-header">
            <span className={`dd-coverage-reason dd-coverage-reason-${reason}`}>
              {REASON_LABEL[reason]}
            </span>
            <span className="dd-muted">{group.length}</span>
          </div>
          <p className="dd-coverage-queue-blurb">{REASON_BLURB[reason]}</p>
          <ul className="dd-coverage-queue-items">
            {group.map(item => <ReviewQueueItemRow key={item.id} item={item} />)}
          </ul>
        </div>
      ))}
    </section>
  );
}

function ReviewQueueItemRow({ item }: { item: CoverageReviewItem }) {
  return (
    <li className="dd-coverage-queue-item">
      <div className="dd-coverage-queue-item-head">
        <span className={`dd-coverage-verdict dd-coverage-verdict-${item.verdictKind}`}>
          {item.verdictKind}
        </span>
        <code className="dd-coverage-queue-target">{item.verdictTarget}</code>
        <span className="dd-coverage-queue-time">{relativeTime(item.addedAt)}</span>
      </div>
      {item.rationale && <div className="dd-coverage-queue-rationale">{item.rationale}</div>}
      {item.detail && <div className="dd-coverage-queue-detail">{item.detail}</div>}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Generic stat tile
// ─────────────────────────────────────────────────────────────────────

interface StatProps {
  label: string;
  value: number | string;
  small?: boolean;
}

function Stat({ label, value, small }: StatProps) {
  return (
    <div className={`dd-coverage-stat ${small ? 'dd-coverage-stat-small' : ''}`}>
      <div className="dd-coverage-stat-label">{label}</div>
      <div className="dd-coverage-stat-value">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────────────

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms) || ms < 0) return iso;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
